import isMergedCell from 'notebook/structured-tables/util/isMergedCell';
import {Table} from 'notebook/tables/constants/TableConstants';
import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import makeTableHTML from 'notebook/tables/export/makeTableHTML';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';

/**
 * Serialization of the current selection for the clipboard.
 */

export interface ComputeCopySelectionParams {
  /**
   * Whether the selection lies within the table's real bounds. A selection that reaches into the
   * ghost cells has no cells to serialize, so nothing is copied.
   */
  hasRealSelection: boolean;
  /**
   * The selection to serialize, expanded to fully contain any intersecting merged regions. Null
   * when nothing is selected, in which case there is nothing to copy.
   */
  expandedBox: MultiSelectionBox | null;
  table: Table;
  computedTable: Array<Array<EvaluatedCell>>;
  /** Whether to copy the cells' formulas rather than their evaluated values. */
  showFormulas: boolean;
}

/**
 * Serializes the selection as tab/newline separated plain text, one line per row.
 *
 * The cells a merged region covers are serialized as empty, so that the merge anchor's value is not
 * repeated across the region.
 */
export function computeCopySelectionText({
  hasRealSelection,
  expandedBox,
  table,
  computedTable,
  showFormulas,
}: ComputeCopySelectionParams): string {
  if (!hasRealSelection || expandedBox == null) {
    return '';
  }
  const {minI, maxI, minJ, maxJ} = expandedBox;
  const mergedCellsByIndex: {[key: string]: boolean} = {};
  const rows = [];
  for (let i = minI; i <= maxI; i++) {
    const row = [];
    for (let j = minJ; j <= maxJ; j++) {
      const cell = table.table[i][j];
      const isHiddenMergedCell = mergedCellsByIndex[`${i},${j}`];
      if (!isHiddenMergedCell && isMergedCell(cell)) {
        // Mark all the cells in the merged range as merged
        for (let mergeRowIndex = 0; mergeRowIndex < (cell.mergeRowSpan || 1); mergeRowIndex++) {
          for (let mergeColIndex = 0; mergeColIndex < (cell.mergeColSpan || 1); mergeColIndex++) {
            mergedCellsByIndex[`${i + mergeRowIndex},${j + mergeColIndex}`] = true;
          }
        }
      }
      // Don't include a value for hidden merged cells
      if (isHiddenMergedCell) {
        row.push('');
      } else {
        row.push(showFormulas ? cell.text : formatTypedValue(computedTable[i][j].value, cell));
      }
    }
    rows.push(row.join('\t'));
  }
  return rows.join('\n');
}

export interface ComputeCopySelectionHtmlParams extends ComputeCopySelectionParams {
  /** Whether the table interprets cell text as formulas at all. */
  disableFormulas: boolean;
}

/**
 * Serializes the selection as an HTML table, which preserves formulas and formatting when pasted
 * back into a Benchling table.
 */
export function computeCopySelectionHtml({
  hasRealSelection,
  expandedBox,
  table,
  computedTable,
  disableFormulas,
  showFormulas,
}: ComputeCopySelectionHtmlParams): string {
  if (!hasRealSelection || expandedBox == null) {
    return '';
  }
  const $table = makeTableHTML(table, computedTable, expandedBox, {disableFormulas, showFormulas});
  return $table[0].outerHTML;
}
